/**
 * AdminAuditVatPage — Phase 1E Option B (2026-09-02).
 *
 * Page admin one-shot pour lancer l'audit rétroactif TVA du tenant courant.
 *
 * États :
 *   - loading : fetch initial du status
 *   - ready : bouton "Lancer l'audit TVA" actif
 *   - running : POST en cours, spinner
 *   - success : rapport JSON structuré en 4 sections gravité
 *   - already_executed : bandeau informatif "audit déjà exécuté le … par …"
 *   - error : message d'erreur clair
 *
 * Idempotence : le backend renvoie 410 Gone au 2e appel. La page prévient en
 * checkant l'endpoint `/status` au chargement, mais catch aussi le 410 si le
 * user clique quand même (concurrent access).
 *
 * Règle produit (DECISIONS.md § Phase 1E) : le bouton de ré-armement n'est
 * JAMAIS exposé ici. Backdoor CLI/curl uniquement pour ré-armement admin.
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Lock,
  ShieldAlert,
  Play,
  FileWarning,
  Files,
  Send,
  FileText,
  ExternalLink,
} from "lucide-react";
import { formatCurrencyBE } from "@/lib/currency";

// ------------------------------------------------------------------ Sections
const SECTIONS = [
  {
    key: "1_factures_emises",
    label: "Factures émises",
    subtitle: "Impact comptable réel — rattrapage par avoir",
    badgeClass: "badge-error",
    icon: FileWarning,
    linkPrefix: "/factures/",
  },
  {
    key: "2_devis_signes",
    label: "Devis signés",
    subtitle: "Engagement client — négo ou avoir anticipé",
    badgeClass: "badge-warning",
    icon: Files,
    linkPrefix: "/quotes/",
  },
  {
    key: "3_devis_envoyes",
    label: "Devis envoyés (non signés)",
    subtitle: "Rattrapable — renvoyer une version corrigée",
    badgeClass: "badge-warning",
    icon: Send,
    linkPrefix: "/quotes/",
  },
  {
    key: "4_devis_brouillons",
    label: "Brouillons",
    subtitle: "Correction 2 clics — PATCH devis",
    badgeClass: "badge-neutral",
    icon: FileText,
    linkPrefix: "/quotes/",
  },
];

// ------------------------------------------------------------------ Helpers
function formatDateFR(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-BE", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function groupLinesByPriority(affected) {
  const groups = { "1_factures_emises": [], "2_devis_signes": [], "3_devis_envoyes": [], "4_devis_brouillons": [] };
  for (const row of affected || []) {
    const key = Object.keys(groups).find((k) => k.startsWith(String(row.priority) + "_"));
    if (key) groups[key].push(row);
  }
  return groups;
}

// ------------------------------------------------------------------ SectionBlock
function SectionBlock({ section, rows, summary }) {
  const Icon = section.icon;
  const hasRows = rows && rows.length > 0;
  const count = summary?.count ?? 0;
  const overDeclared = summary?.total_over_declared_eur ?? 0;
  const tvac = summary?.total_tvac_eur ?? 0;

  return (
    <Card
      className="border-slate-800 bg-slate-900/40"
      data-testid={`admin-audit-section-${section.key}`}
    >
      <CardContent className="p-4 sm:p-6">
        <div className="flex flex-col gap-3 border-b border-slate-800 pb-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <Icon className="h-5 w-5 text-slate-300" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-slate-100 sm:text-lg">
                  {section.label}
                </h2>
                <Badge className={section.badgeClass}>{count} lignes</Badge>
              </div>
              <p className="mt-1 text-xs text-slate-400 sm:text-sm">{section.subtitle}</p>
            </div>
          </div>
          <div className="text-left sm:text-right">
            <div className="text-xs uppercase tracking-wider text-slate-500">TVA sur-déclarée</div>
            <div
              className={`text-xl font-bold sm:text-2xl ${
                overDeclared > 0 ? "text-red-300" : "text-slate-400"
              }`}
            >
              {overDeclared > 0 ? "+" : ""}
              {formatCurrencyBE(overDeclared)}
            </div>
            <div className="text-xs text-slate-500">
              sur TVAC {formatCurrencyBE(tvac)}
            </div>
          </div>
        </div>

        {!hasRows ? (
          <div className="flex items-center gap-2 pt-4 text-sm text-slate-500">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            Aucune ligne affectée dans cette catégorie.
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="pb-2 pr-4 text-left font-medium">Référence</th>
                  <th className="pb-2 pr-4 text-left font-medium">Client</th>
                  <th className="pb-2 pr-4 text-left font-medium">Date</th>
                  <th className="pb-2 pr-4 text-right font-medium">TVAC</th>
                  <th className="pb-2 pr-4 text-right font-medium">Lignes</th>
                  <th className="pb-2 pr-2 text-right font-medium">Sur-déclaré</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className="hover:bg-slate-800/40"
                    data-testid={`admin-audit-row-${row.id}`}
                  >
                    <td className="py-3 pr-4">
                      <Link
                        to={`${section.linkPrefix}${row.id}`}
                        className="inline-flex items-center gap-1.5 font-medium text-sky-400 hover:text-sky-300 hover:underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {row.reference || `#${row.id.slice(0, 8)}`}
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                    </td>
                    <td className="py-3 pr-4 text-slate-200">{row.client_name || "—"}</td>
                    <td className="py-3 pr-4 text-slate-400">
                      {row.sent_at || row.invoiced_at || row.created_at || "—"}
                    </td>
                    <td className="py-3 pr-4 text-right text-slate-300">
                      {formatCurrencyBE(row.total_tvac_eur)}
                    </td>
                    <td className="py-3 pr-4 text-right text-slate-300">
                      {row.affected_lines_count}
                    </td>
                    <td className="py-3 pr-2 text-right font-medium text-red-300">
                      +{formatCurrencyBE(row.vat_over_declared_eur)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------------------ Page principale
export default function AdminAuditVatPage() {
  const [statusState, setStatusState] = useState("loading"); // loading | ready | already_executed | error
  const [statusData, setStatusData] = useState(null);
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");

  const tenantSlug = "lldls";

  // Fetch initial status au chargement
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(
          `/admin/audit/vat-mismatch/status?tenant_slug=${tenantSlug}`,
        );
        setStatusData(data);
        setStatusState(data.status === "already_executed" ? "already_executed" : "ready");
      } catch (e) {
        setError(formatApiError(e));
        setStatusState("error");
      }
    })();
  }, []);

  const runAudit = async () => {
    setRunning(true);
    setError("");
    try {
      const { data } = await api.post("/admin/audit/vat-mismatch", {
        tenant_slug: tenantSlug,
        include_details: true,
      });
      setReport(data);
      // Update status en local pour refléter que c'est fait
      setStatusState("already_executed");
      setStatusData({
        status: "already_executed",
        executed_at: data.audit_at,
        executed_by_email: "Vous (à l'instant)",
        summary_by_priority: data.summary_by_priority,
        total_affected_documents: data.total_affected_documents,
        total_affected_lines: data.total_affected_lines,
      });
      toast.success("Audit TVA exécuté.");
    } catch (e) {
      const status = e?.response?.status;
      const body = e?.response?.data;
      if (status === 410) {
        // Concurrent : un autre admin a déjà lancé pendant qu'on lisait
        setStatusState("already_executed");
        setStatusData({
          status: "already_executed",
          executed_at: body?.executed_at,
          executed_by_email: body?.executed_by_email,
        });
        toast.warning("Un autre admin a déjà lancé l'audit.");
      } else {
        setError(formatApiError(e));
        toast.error(formatApiError(e));
      }
    } finally {
      setRunning(false);
    }
  };

  const groupedRows = report ? groupLinesByPriority(report.affected_lines) : {};
  const summary = report?.summary_by_priority || statusData?.summary_by_priority || null;

  return (
    <div className="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-6" data-testid="admin-audit-page">
      {/* En-tête */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
          <ShieldAlert className="h-4 w-4" />
          Administration
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-100 sm:text-3xl">
              Audit rétroactif TVA
            </h1>
            <p className="mt-1 text-sm text-slate-400">
              Détecte les lignes de devis + factures dont le taux TVA diverge du taux
              par défaut de l'entreprise. Lecture seule stricte. One-shot idempotent.
            </p>
          </div>
          <Badge className="badge-neutral self-start sm:self-auto">
            Tenant&nbsp;: {tenantSlug.toUpperCase()}
          </Badge>
        </div>
      </div>

      {/* État loading initial */}
      {statusState === "loading" && (
        <Card className="border-slate-800 bg-slate-900/40">
          <CardContent className="flex items-center justify-center gap-3 p-8 text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
            Vérification de l'état de l'audit…
          </CardContent>
        </Card>
      )}

      {/* État erreur */}
      {statusState === "error" && (
        <Card className="border-red-800 bg-red-950/30">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 text-red-400" />
              <div>
                <div className="font-medium text-red-300">Erreur de chargement</div>
                <div className="mt-1 text-sm text-red-400/80">{error}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* État ready : bouton lancer audit */}
      {statusState === "ready" && (
        <Card className="border-amber-900/40 bg-amber-950/20">
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
                <div>
                  <div className="font-medium text-amber-200">
                    L'audit n'a pas encore été exécuté sur ce tenant.
                  </div>
                  <p className="mt-1 text-sm text-amber-300/80">
                    Le rapport listera toutes les lignes divergentes triées par gravité
                    de rattrapage. L'endpoint sera désactivé après ce premier appel
                    (ré-armement manuel CLI possible en cas de régression).
                  </p>
                </div>
              </div>
              <Button
                onClick={runAudit}
                disabled={running}
                className="h-14 min-w-[220px] shrink-0 gap-2 bg-amber-600 text-base font-semibold text-white hover:bg-amber-500 disabled:opacity-60"
                data-testid="admin-audit-run-btn"
              >
                {running ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" /> Analyse en cours…
                  </>
                ) : (
                  <>
                    <Play className="h-5 w-5" /> Lancer l'audit TVA
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* État already_executed : bandeau informatif + bouton désactivé */}
      {statusState === "already_executed" && !report && (
        <Card className="border-slate-800 bg-slate-900/40">
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <Lock className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
                <div>
                  <div className="font-medium text-slate-100">
                    Audit déjà exécuté
                  </div>
                  <p className="mt-1 text-sm text-slate-400">
                    Exécuté le {formatDateFR(statusData?.executed_at)} par{" "}
                    <span className="font-medium text-slate-300">
                      {statusData?.executed_by_email}
                    </span>
                    .
                  </p>
                  <p className="mt-2 text-xs text-slate-500">
                    L'endpoint est désactivé. Un ré-armement manuel (CLI admin) est
                    requis pour relancer l'audit.
                  </p>
                </div>
              </div>
              <Button
                disabled
                className="h-14 min-w-[220px] shrink-0 gap-2 bg-slate-700 text-base font-semibold text-slate-400"
                data-testid="admin-audit-run-btn"
              >
                <Lock className="h-5 w-5" /> Audit verrouillé
              </Button>
            </div>

            {/* Résumé de l'audit précédent si dispo (sans les détails ligne-par-ligne) */}
            {summary && (
              <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {SECTIONS.map((sec) => {
                  const agg = summary[sec.key] || { count: 0, total_over_declared_eur: 0 };
                  return (
                    <div
                      key={sec.key}
                      className="rounded-lg border border-slate-800 bg-slate-950/50 p-3"
                    >
                      <div className="text-xs uppercase tracking-wider text-slate-500">
                        {sec.label}
                      </div>
                      <div className="mt-1 text-lg font-semibold text-slate-100">
                        {agg.count} lignes
                      </div>
                      <div
                        className={`text-xs ${
                          agg.total_over_declared_eur > 0 ? "text-red-300" : "text-slate-500"
                        }`}
                      >
                        {agg.total_over_declared_eur > 0 ? "+" : ""}
                        {formatCurrencyBE(agg.total_over_declared_eur)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Rapport fraîchement exécuté : 4 sections détaillées */}
      {report && (
        <div className="space-y-4">
          <Card className="border-emerald-900/40 bg-emerald-950/20">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
                <div>
                  <div className="font-medium text-emerald-200">
                    Audit exécuté avec succès
                  </div>
                  <p className="mt-1 text-sm text-emerald-300/80">
                    Tenant <span className="font-medium">{report.tenant}</span> · Taux
                    par défaut <span className="font-medium">{report.default_vat_rate}%</span> ·{" "}
                    {report.total_affected_documents} document
                    {report.total_affected_documents > 1 ? "s" : ""} affecté
                    {report.total_affected_documents > 1 ? "s" : ""} ·{" "}
                    {report.total_affected_lines} ligne
                    {report.total_affected_lines > 1 ? "s" : ""} divergente
                    {report.total_affected_lines > 1 ? "s" : ""}
                  </p>
                  {report.truncated && (
                    <div className="mt-2 text-xs text-amber-300">
                      ⚠ Résultat tronqué à 1000 lignes.
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {SECTIONS.map((section) => (
            <SectionBlock
              key={section.key}
              section={section}
              rows={groupedRows[section.key] || []}
              summary={report.summary_by_priority?.[section.key]}
            />
          ))}
        </div>
      )}
    </div>
  );
}
